<osp-stepper [formGroup]="form" #stepper>
  <!-- STEP #1 - PROJECT DATA -->
  <osp-step
    [title]="'modules.locations.project.create.projectData.title' | translate"
    [icon]="IconKey.EquipmentCreateMainData | icon"
    [control]="form.controls.projectData"
  >
    <ng-template ospStepContent>
      <div class="step-content" [formGroup]="form.controls.projectData">
        <div class="grid">
          <div class="col-9">
            <div class="grid mb-2">
              <osp-form-field class="col-6">
                <osp-label>{{ 'modules.locations.project.create.form.projectName.label' | translate }}</osp-label>
                <osp-input-text
                  formControlName="projectName"
                  [placeholder]="'modules.locations.project.create.form.projectName.placeholder' | translate"
                />
                <osp-error [messages]="formService.projectNameErrorMessages" />
              </osp-form-field>

              <osp-form-field class="col-3">
                <osp-label>{{ 'general.startDate' | translate }}</osp-label>
                <osp-input-date
                  formControlName="projectStart"
                  [placeholder]="'modules.locations.project.create.form.startDate.placeholder' | translate"
                  [showIcon]="true"
                />
                <osp-error [messages]="formService.projectStartErrorMessages" />
                <osp-error
                  [errors]="form.controls.projectData.errors"
                  [messages]="formService.projectDateRangeErrorMessages"
                />
              </osp-form-field>

              <osp-form-field class="col-3">
                <osp-label>{{ 'general.endDate' | translate }}</osp-label>
                <osp-input-date
                  formControlName="projectEnd"
                  [placeholder]="'modules.locations.project.create.form.endDate.placeholder' | translate"
                  [showIcon]="true"
                  [minDate]="form.controls.projectData.controls.projectStart.value || undefined"
                />
                <osp-error
                  [errors]="form.controls.projectData.errors"
                  [messages]="formService.projectDateRangeErrorMessages"
                />
              </osp-form-field>
            </div>

            <div class="grid mb-2">
              <osp-form-field class="col-6">
                <osp-label>{{ 'general.org.s' | translate }}</osp-label>
                <osp-dropdown
                  formControlName="organisationId"
                  [options]="$organisations()"
                  [placeholder]="'modules.equipment.equipmentCreate.form.organisation.placeholder' | translate"
                  [showClear]="true"
                  [filter]="true"
                />
                <osp-error [messages]="formService.organisationErrorMessages" />
              </osp-form-field>

              <osp-form-field class="col-6">
                <osp-label>{{ 'modules.locations.project.create.form.type.label' | translate }}</osp-label>
                <osp-dropdown
                  formControlName="projectType"
                  [options]="projectTypeOptions"
                  optionLabel="label"
                  optionValue="type"
                  [placeholder]="'modules.locations.project.create.form.type.placeholder' | translate"
                  [showClear]="true"
                />
                <osp-error [messages]="formService.projectTypeErrorMessages" />
              </osp-form-field>
            </div>

            <div class="grid mb-2">
              <osp-form-field class="col-6">
                <osp-label>{{ 'modules.locations.project.create.form.costCenter.label' | translate }}</osp-label>
                <osp-input-text
                  formControlName="projectCostCenter"
                  [placeholder]="'modules.locations.project.create.form.costCenter.placeholder' | translate"
                />
                <osp-error [messages]="formService.projectCostCenterErrorMessages" />
              </osp-form-field>
            </div>

            <div class="grid">
              <osp-form-field class="col-12">
                <osp-label>{{ 'general.description' | translate }}</osp-label>
                <osp-input-textarea
                  formControlName="description"
                  [placeholder]="'modules.locations.project.create.form.description.placeholder' | translate"
                  [rows]="3"
                />
                <osp-error [messages]="formService.descriptionErrorMessages" />
              </osp-form-field>
            </div>
          </div>
          <div class="step-content-form-card col-3">
            <ng-container
              [ngTemplateOutlet]="infoBox"
              [ngTemplateOutletContext]="{
                label: locationInfoLabel,
                icon: IconKey.ProjectCreateMain | icon,
                description: locationInfoDescription
              }"
            />
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template ospStepFooter let-prevCallback="prevCallback" let-nextCallback="nextCallback" let-isValid="isValid">
      <div class="flex align-items-center justify-content-between">
        <ng-container [ngTemplateOutlet]="btnCancel" />
        @if (isEditMode) {
          <ng-container [ngTemplateOutlet]="btnSave" [ngTemplateOutletContext]="{ disabled: !isValid }" />
        } @else {
          <div class="flex gap-2">
            <ng-container [ngTemplateOutlet]="btnBack" [ngTemplateOutletContext]="{ onClick: goBackToItemSelection }" />
            <ng-container
              [ngTemplateOutlet]="btnNext"
              [ngTemplateOutletContext]="{ onClick: nextCallback, disabled: !isValid }"
            />
          </div>
        }
      </div>
    </ng-template>
  </osp-step>

  <!-- STEP #2 - ADDRESS -->
  <osp-step
    [title]="'modules.contact.base.contactType.location' | translate"
    [icon]="IconKey.ProjectCreateMain | icon"
    [control]="form.controls.projectAddress"
  >
    <ng-template ospStepContent>
      <div class="step-content" [formGroup]="form.controls.projectAddress">
        <div class="grid">
          <div class="col-9">
            <osp-location-address-form
              [formGroup]="form.controls.projectAddress"
              [mapCenter]="mapCenter"
              [mapMarkerPosition]="mapMarkerPosition"
              [isMapLoaded]="(isMapLoaded$ | async) ?? false"
              [showAutomaticGeofence]="showAutomaticGeofence"
              (mapClick)="handleMapClick($event)"
              (locationChanges)="handleSearchLocationChange($event)"
            />
          </div>
          <div class="step-content-form-card col-3">
            <ng-container
              [ngTemplateOutlet]="infoBox"
              [ngTemplateOutletContext]="{
                label: locationInfoLabel,
                icon: IconKey.ProjectCreateMain | icon,
                description: locationInfoDescription
              }"
            />
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template ospStepFooter let-prevCallback="prevCallback" let-nextCallback="nextCallback" let-isValid="isValid">
      <div class="flex align-items-center justify-content-between">
        <ng-container [ngTemplateOutlet]="btnCancel" />
        <div class="flex gap-2">
          <ng-container [ngTemplateOutlet]="btnBack" [ngTemplateOutletContext]="{ onClick: prevCallback }" />
          <ng-container [ngTemplateOutlet]="btnSave" [ngTemplateOutletContext]="{ disabled: !isValid }" />
        </div>
      </div>
    </ng-template>
  </osp-step>
</osp-stepper>

<!-- Templates -->
<ng-template #infoBox let-label="label" let-icon="icon" let-description="description">
  <osp-card-option [label]="label" [icon]="icon" [description]="description" />
</ng-template>

<ng-template #btnCancel>
  <osp-button
    [label]="'general.buttons.cancel' | translate"
    [icon]="IconKey.GlobalCancel | icon"
    [category]="'ghost'"
    (onClick)="onCancel()"
  />
</ng-template>

<ng-template #btnBack let-onClick="onClick">
  <osp-button
    [label]="'general.buttons.back' | translate"
    [icon]="IconKey.GlobalFormBack | icon"
    [category]="'secondary'"
    (onClick)="onClick()"
  />
</ng-template>

<ng-template #btnNext let-onClick="onClick" let-disabled="disabled">
  <osp-button
    [label]="'general.buttons.next' | translate"
    [icon]="IconKey.GlobalFormNext | icon"
    (onClick)="onClick()"
    [disabled]="disabled"
  />
</ng-template>

<ng-template #btnSave let-disabled="disabled">
  <osp-button
    [label]="'general.buttons.save' | translate"
    [icon]="IconKey.GlobalSave | icon"
    (onClick)="handleSubmit()"
    [disabled]="disabled || isSubmitDisabled"
  />
</ng-template>
